import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { Box, Grid, Paper, Typography } from "@mui/material"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as FileIcon } from "src/v2/assets/icons/file-icon.svg"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import PriceService from "src/services/price-service"
import { DATE_FORMAT } from "src/utils/constants"
import TabelaPrecoBonds from "./components/tabela-preco-bonds"

const TITLE = "Tabela de PU/Bonds"
const LAST_DATE_ERROR_MESSAGE = "Não foi possível carregar a última data disponível para Bonds."
const INVALID_DATE_MESSAGE = "Informe uma data válida para consultar os preços de Bonds."
const DATE_CONFIRMATION_MESSAGE = "Pressione Enter para atualizar a tabela."

const PrecosPuBondsPage = () => {
    const priceService = useMemo(() => new PriceService(), [])
    const [loading, setLoading] = useState(false)
    const [dateSelected, setDateSelected] = useState("")
    const [lastAvailableDate, setLastAvailableDate] = useState("")
    const [lastDateStatus, setLastDateStatus] = useState("loading")
    const [lastDateRetryVersion, setLastDateRetryVersion] = useState(0)
    const [dateError, setDateError] = useState("")
    const [datePendingConfirmation, setDatePendingConfirmation] = useState(false)

    useEffect(() => {
        let ignoreResponse = false

        const loadLastDate = async () => {
            setLoading(true)
            setLastDateStatus("loading")

            try {
                const lastDate = await priceService.loadBondsLastDate()
                const parsedDate = dayjs(lastDate)

                if (ignoreResponse) return

                if (!lastDate || !parsedDate.isValid()) {
                    setDateSelected("")
                    setLastAvailableDate("")
                    setDateError("")
                    setDatePendingConfirmation(false)
                    setLastDateStatus("empty")
                    return
                }

                const formattedLastDate = parsedDate.format(DATE_FORMAT)
                setLastAvailableDate(formattedLastDate)
                setDateSelected(formattedLastDate)
                setDateError("")
                setDatePendingConfirmation(false)
                setLastDateStatus("success")
            } catch {
                if (ignoreResponse) return

                setDateSelected("")
                setLastAvailableDate("")
                setDateError("")
                setDatePendingConfirmation(false)
                setLastDateStatus("error")
                dispatchSnackbar(LAST_DATE_ERROR_MESSAGE, "error")
            } finally {
                if (!ignoreResponse) setLoading(false)
            }
        }

        loadLastDate()

        return () => {
            ignoreResponse = true
        }
    }, [lastDateRetryVersion, priceService])

    const getDateError = (value) => {
        const parsedDate = dayjs(value)

        if (!value || !parsedDate.isValid()) {
            return INVALID_DATE_MESSAGE
        }

        if (
            lastAvailableDate &&
            parsedDate.isAfter(dayjs(lastAvailableDate), "day")
        ) {
            return `A última data disponível para Bonds é ${dayjs(lastAvailableDate).format("DD/MM/YYYY")}. Selecione uma data igual ou anterior.`
        }

        return ""
    }

    const handleDateDraftChange = (value) => {
        const error = getDateError(value)
        const isCurrentSelectedDate =
            !error &&
            dateSelected &&
            dayjs(value).isSame(dayjs(dateSelected), "day")

        setDateError(error)
        setDatePendingConfirmation(!error && !isCurrentSelectedDate)
    }

    const changeDate = (value) => {
        const error = getDateError(value)

        if (error) {
            setDateError(error)
            setDatePendingConfirmation(false)
            return
        }

        setDateError("")
        setDatePendingConfirmation(false)
        setDateSelected(dayjs(value).format(DATE_FORMAT))
    }

    const exportFile = async () => {
        if (!dateSelected || dateError || datePendingConfirmation || loading) return

        setLoading(true)
        try {
            const exported = await priceService.exportBondsDataToCsv(dateSelected)

            if (!exported) {
                dispatchSnackbar("Não há dados de Bonds para exportar.", "warning")
            }
        } catch {
            dispatchSnackbar("Não foi possível exportar os dados de Bonds.", "error")
        } finally {
            setLoading(false)
        }
    }

    const renderContent = () => {
        if (lastDateStatus === "error") {
            return (
                <Box
                    role="alert"
                    aria-live="assertive"
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="textBody" sx={{ color: "error.light" }}>
                        {LAST_DATE_ERROR_MESSAGE}
                    </Typography>
                    <ButtonWithIcon
                        size="md"
                        onClick={() => setLastDateRetryVersion((value) => value + 1)}
                    >
                        Tentar novamente
                    </ButtonWithIcon>
                </Box>
            )
        }

        if (lastDateStatus === "empty") {
            return (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="textBody">
                        Não há uma data de referência disponível para Bonds.
                    </Typography>
                </Box>
            )
        }

        if (!dateSelected) return null

        return <TabelaPrecoBonds date={dateSelected} setLoading={setLoading} />
    }

    return (
        <SideBarLayout
            title={TITLE}
            header
            variant="v1"
            loading={loading}
            disablePageScroll
            headerImage="/background/eventos_headers.jpg"
            headerPosition="1% 42%"
            headerTitle={TITLE}
        >
            <Box
                sx={{
                    mt: -2,
                    height: "calc(100dvh - 202px)",
                    maxHeight: "calc(100dvh - 202px)",
                    minHeight: 0,
                    pb: "8px",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    gap: 2,
                }}
            >
                <Grid container sx={{ flexShrink: 0 }}>
                    <Grid item xs={12} xl={10}>
                        <Paper sx={{ p: 3 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={2}
                            >
                                <Grid item xs={12} sm={6} md={3}>
                                    {lastDateStatus === "success" && (
                                        <DatePickerSelectV2
                                            value={dateSelected}
                                            label="Data de Exibição"
                                            onChange={changeDate}
                                            onDraftChange={handleDateDraftChange}
                                            commitOnAccept
                                            maxDate={lastAvailableDate}
                                            error={Boolean(dateError)}
                                            helperText={
                                                dateError ||
                                                (datePendingConfirmation
                                                    ? DATE_CONFIRMATION_MESSAGE
                                                    : undefined)
                                            }
                                        />
                                    )}
                                </Grid>
                                <Grid item>
                                    <ButtonWithIcon
                                        size="md"
                                        icon={<FileIcon />}
                                        onClick={exportFile}
                                        disabled={
                                            !dateSelected ||
                                            Boolean(dateError) ||
                                            datePendingConfirmation ||
                                            loading
                                        }
                                    >
                                        Exportar CSV
                                    </ButtonWithIcon>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper
                    sx={{
                        flex: 1,
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    {renderContent()}
                </Paper>
            </Box>
        </SideBarLayout>
    )
}

export default PrecosPuBondsPage
